import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../../components/Can';
import { useCan } from '../../../hooks/useCan';
import crmAdjuntoService from '../../../services/crmAdjuntoService';
import type { CrmOportunidadAdjunto } from '../../../types/crm';
import { getApiErrorMessage } from '../../../utils/apiError';

const ACCEPT =
  '.pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(a: CrmOportunidadAdjunto) {
  if (a.esImagen) return <ImageIcon fontSize="small" color="primary" />;
  if (a.esPdf) return <PdfIcon fontSize="small" color="error" />;
  return <FileIcon fontSize="small" color="action" />;
}

export interface CrmAdjuntosPanelProps {
  oportunidadId: number;
  compact?: boolean;
  /** Oculta el título interno (p. ej. cuando el accordion padre ya dice «Adjuntos»). */
  hideHeader?: boolean;
  onCountChange?: (count: number) => void;
}

const CrmAdjuntosPanel: React.FC<CrmAdjuntosPanelProps> = ({
  oportunidadId,
  compact = false,
  hideHeader = false,
  onCountChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const canEditar = useCan('crm.oportunidades.editar');
  const inputRef = useRef<HTMLInputElement>(null);
  const onCountChangeRef = useRef(onCountChange);
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  const lastCountRef = useRef<number | null>(null);
  onCountChangeRef.current = onCountChange;
  enqueueSnackbarRef.current = enqueueSnackbar;

  const [items, setItems] = useState<CrmOportunidadAdjunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<{ url: string; nombre: string; esImagen: boolean } | null>(
    null,
  );

  const notifyCount = useCallback((count: number) => {
    if (lastCountRef.current === count) return;
    lastCountRef.current = count;
    onCountChangeRef.current?.(count);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await crmAdjuntoService.list(oportunidadId);
      setItems(list);
      notifyCount(list.length);
    } catch (error) {
      enqueueSnackbarRef.current(getApiErrorMessage(error, 'Error al cargar adjuntos'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [oportunidadId, notifyCount]);

  useEffect(() => {
    lastCountRef.current = null;
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const file of list) {
        await crmAdjuntoService.upload(oportunidadId, file);
      }
      enqueueSnackbar(
        list.length === 1 ? 'Archivo subido' : `${list.length} archivos subidos`,
        { variant: 'success' },
      );
      await load();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo adjuntar el archivo'), {
        variant: 'error',
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const openFile = async (a: CrmOportunidadAdjunto, asDownload: boolean) => {
    try {
      const blob = await crmAdjuntoService.fetchBlob(oportunidadId, a.id, asDownload);
      const url = URL.createObjectURL(blob);
      if (!asDownload && (a.esImagen || a.esPdf)) {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview({ url, nombre: a.nombreOriginal, esImagen: a.esImagen });
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = a.nombreOriginal;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo abrir el archivo'), { variant: 'error' });
    }
  };

  const handleDelete = async (a: CrmOportunidadAdjunto) => {
    try {
      await crmAdjuntoService.remove(oportunidadId, a.id);
      enqueueSnackbar('Archivo eliminado', { variant: 'success' });
      await load();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo eliminar'), { variant: 'error' });
    }
  };

  return (
    <Box>
      {!hideHeader && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: compact ? 1 : 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AttachFileIcon fontSize="small" color="action" />
            <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 600 }}>
              Adjuntos
            </Typography>
            {items.length > 0 && (
              <Chip size="small" label={items.length} color="primary" sx={{ height: 20, fontWeight: 700 }} />
            )}
          </Stack>
          <Can permiso="crm.oportunidades.editar">
            <Button
              size="small"
              variant="text"
              startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Subir
            </Button>
          </Can>
        </Stack>
      )}
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        accept={ACCEPT}
        onChange={(e) => {
          if (e.target.files) void uploadFiles(e.target.files);
        }}
      />

      <Can permiso="crm.oportunidades.editar">
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.length) void uploadFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          sx={{
            mb: items.length > 0 || loading ? 1.5 : 0,
            py: compact ? 1.25 : 1.75,
            px: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'rgba(25,118,210,0.06)' : 'rgba(0,0,0,0.015)',
            cursor: uploading ? 'wait' : 'pointer',
            opacity: uploading ? 0.7 : 1,
            transition: 'border-color 0.2s, background-color 0.2s',
            textAlign: 'center',
            '&:hover': {
              borderColor: 'primary.light',
              bgcolor: 'rgba(25,118,210,0.04)',
            },
          }}
        >
          {uploading ? (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Subiendo…
              </Typography>
            </Stack>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Soltá archivos acá o hacé click · PDF, Excel, Word o imágenes
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Máx. 10 MB por archivo
              </Typography>
            </>
          )}
        </Box>
      </Can>

      {loading ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: hideHeader ? 1 : 0 }}>
          Cargando…
        </Typography>
      ) : items.length === 0 ? (
        !canEditar ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
            Todavía no hay archivos.
          </Typography>
        ) : null
      ) : (
        <Stack spacing={0.75}>
          {items.map((a) => (
            <Stack
              key={a.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                '&:hover': { bgcolor: 'rgba(25,118,210,0.03)' },
              }}
            >
              {fileIcon(a)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => void openFile(a, false)}
                  sx={{
                    display: 'block',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    border: 0,
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                  }}
                >
                  {a.nombreOriginal}
                </Link>
                <Typography variant="caption" color="text.secondary">
                  {formatBytes(a.tamanoBytes)}
                  {a.usuarioAlta ? ` · ${a.usuarioAlta}` : ''}
                </Typography>
              </Box>
              <IconButton
                size="small"
                title={a.esImagen || a.esPdf ? 'Ver' : 'Abrir'}
                onClick={() => void openFile(a, !(a.esImagen || a.esPdf))}
              >
                {a.esImagen || a.esPdf ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
              {(a.esImagen || a.esPdf) && (
                <IconButton size="small" title="Descargar" onClick={() => void openFile(a, true)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              )}
              <Can permiso="crm.oportunidades.editar">
                <IconButton size="small" color="error" title="Eliminar" onClick={() => void handleDelete(a)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Can>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog
        open={Boolean(preview)}
        onClose={() => {
          if (preview?.url) URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {preview?.nombre}
          </Typography>
          {preview?.esImagen ? (
            <Box
              component="img"
              src={preview.url}
              alt={preview.nombre}
              sx={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', mx: 'auto', borderRadius: 1 }}
            />
          ) : preview ? (
            <Box
              component="iframe"
              src={preview.url}
              title={preview.nombre}
              sx={{ width: '100%', height: '70vh', border: 0, borderRadius: 1 }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CrmAdjuntosPanel;
